import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  CircularProgress,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Divider,
} from '@mui/material';
import { withdrawalsAPI } from '../../services/stripeAPI';

interface WithdrawalDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export default function WithdrawalDialog({ open, onClose, onSuccess }: WithdrawalDialogProps) {
  const [amount, setAmount] = useState<string>('50.00');
  const [instantPayout, setInstantPayout] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [availableBalance, setAvailableBalance] = useState<number>(0);
  const [loadingBalance, setLoadingBalance] = useState(true);

  useEffect(() => {
    if (open) {
      loadBalance();
    }
  }, [open]);

  const loadBalance = async () => {
    try {
      setLoadingBalance(true);
      const data = await withdrawalsAPI.getAvailableBalance();
      setAvailableBalance(data.availableBalance);
    } catch (err: any) {
      setError('Failed to load balance');
    } finally {
      setLoadingBalance(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const amountNum = parseFloat(amount);

    // Validate amount
    if (isNaN(amountNum) || amountNum < 10) {
      setError('Minimum withdrawal amount is $10');
      return;
    }

    if (amountNum > 10000) {
      setError('Maximum withdrawal amount is $10,000');
      return;
    }

    if (amountNum > availableBalance) {
      setError(`Insufficient balance. Available: $${availableBalance.toFixed(2)}`);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await withdrawalsAPI.requestWithdrawal(amountNum, instantPayout);

      if (data.success) {
        setSuccess(true);
        setTimeout(() => {
          onSuccess?.();
          onClose();
        }, 3000);
      } else {
        setError(data.error || 'Withdrawal failed');
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.message || 'Withdrawal failed';
      
      // Check if identity verification required
      if (err.response?.data?.requiresVerification) {
        setError('Identity verification required for withdrawals over $100. Please contact support.');
      } else {
        setError(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const calculateFee = () => {
    const amountNum = parseFloat(amount || '0');
    if (instantPayout) {
      // Instant: 1%
      return amountNum * 0.01;
    } else {
      // Standard: FREE
      return 0;
    }
  };

  const calculateNetAmount = () => {
    const amountNum = parseFloat(amount || '0');
    return amountNum - calculateFee();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Withdraw Funds</DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {loadingBalance ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Transfer funds from your 2Way MicroPay balance to your bank account.
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Available Balance:</strong> ${availableBalance.toFixed(2)}
                </Typography>
              </Alert>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mb: 2 }}>
                  Withdrawal initiated successfully! Funds will arrive in {instantPayout ? '30 minutes' : '2-3 business days'}.
                </Alert>
              )}

              <TextField
                fullWidth
                label="Withdrawal Amount"
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start">$</InputAdornment>,
                }}
                inputProps={{
                  min: 10,
                  max: Math.min(10000, availableBalance),
                  step: 0.01,
                }}
                sx={{ mb: 2 }}
                disabled={loading || success}
                helperText={`Min: $10, Max: $${Math.min(10000, availableBalance).toFixed(2)}`}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={instantPayout}
                    onChange={(e) => setInstantPayout(e.target.checked)}
                    disabled={loading || success}
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2">
                      Instant Payout (30 minutes)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      1% fee vs FREE standard (2-3 business days)
                    </Typography>
                  </Box>
                }
                sx={{ mb: 2 }}
              />

              <Divider sx={{ my: 2 }} />

              <Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Withdrawal Amount:
                  </Typography>
                  <Typography variant="body2">
                    ${parseFloat(amount || '0').toFixed(2)}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Fee:
                  </Typography>
                  <Typography variant="body2" color={instantPayout ? 'error' : 'success.main'}>
                    {instantPayout ? `-$${calculateFee().toFixed(2)} (1%)` : 'FREE'}
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" fontWeight="bold">
                    You'll Receive:
                  </Typography>
                  <Typography variant="body1" fontWeight="bold" color="primary">
                    ${calculateNetAmount().toFixed(2)}
                  </Typography>
                </Box>
              </Box>

              {parseFloat(amount || '0') >= 100 && (
                <Alert severity="warning" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Withdrawals over $100 may require identity verification.
                  </Typography>
                </Alert>
              )}
            </>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={loading || success || loadingBalance}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Processing...' : 'Withdraw'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
